import {
  Charge,
  ChargeStatement,
  ChargeStatus,
  CreatePostingInput,
  Posting,
  PostingDTO,
  PostingStatus,
  StatementStatus,
  UpdatePostingInput,
  getReversalCreatePostingInput,
  isPostingReversalOrCancelledByReversal,
  processInChunks,
} from '@rentguru/commons-utils';
import { isConditionalCheckFailedGraphQLError } from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import uniq from 'lodash/uniq';
import { v5 as uuidv5 } from 'uuid';

const CHARGE_DELETION_BLOCKED_BY_STATEMENT_MESSAGE_ID = 'accounting.charge.deleteChargeBlockedByStatement';
const CHARGE_DELETION_BLOCKED_BY_AFFECTATION_MESSAGE_ID = 'accounting.charge.deleteChargeBlockedByAffectation';
const GENERIC_API_ERROR_MESSAGE_ID = 'error.apicall';
const POSTING_REVERSALS_CHUNK_SIZE = 25;
const REVERSAL_POSTING_ID_NAMESPACE = '44daacc1-0555-4b95-8d8e-40340e79dd63';

export class ChargeDeletionBlockedByStatementError extends Error {
  constructor(chargeId: string) {
    super(`Charge ${chargeId} has postings included in a statement`);
    this.name = 'ChargeDeletionBlockedByStatementError';
  }
}

export class ChargeDeletionBlockedByAffectationError extends Error {
  constructor(chargeId: string) {
    super(`Charge ${chargeId} is affected`);
    this.name = 'ChargeDeletionBlockedByAffectationError';
  }
}

interface ChargePostingReversal {
  postingToCancel: Posting;
  reversalInput: CreatePostingInput;
}

const getActiveChargePostings = (chargePostings: Posting[]): Posting[] =>
  chargePostings.filter((posting) => !isPostingReversalOrCancelledByReversal(posting));

/**
 * An affected charge must go back to "to affect" first: its postings may still be being created by the affectation,
 * after the charge update that bumped its version.
 */
export const isChargeDeletionBlockedByAffectation = (charge: Charge): boolean =>
  charge.status === ChargeStatus.AFFECTED;

/**
 * A Charges V2 tenant share lands on a statement through its `tenantLandings`, without setting `statementId`
 * on the posting itself.
 */
export const isPostingInStatement = (posting: Posting): boolean =>
  !isNil(posting.statementId) || (posting.tenantLandings ?? []).some((landing) => !isNil(landing.statementId));

export const isChargeDeletionBlockedByStatement = (
  chargePostings: Posting[],
  chargeStatements: ChargeStatement[]
): boolean =>
  getActiveChargePostings(chargePostings).some(isPostingInStatement) ||
  chargeStatements.some((chargeStatement) => chargeStatement.statement?.status === StatementStatus.DONE);

export const getReversalPostingId = (postingId: string): string => uuidv5(postingId, REVERSAL_POSTING_ID_NAMESPACE);

export const getChargePostingReversals = (chargePostings: Posting[]): ChargePostingReversal[] =>
  getActiveChargePostings(chargePostings).map((posting) => ({
    postingToCancel: posting,
    reversalInput: {
      ...omit(getReversalCreatePostingInput({ ...posting, status: PostingStatus.REVERSAL }), 'tenantLandings'),
      id: getReversalPostingId(posting.id),
    },
  }));

const createReversalUnlessAlreadyCreated = async (
  reversalInput: CreatePostingInput,
  createPosting: (input: CreatePostingInput) => Promise<PostingDTO>
): Promise<void> => {
  try {
    await createPosting(reversalInput);
  } catch (error) {
    if (!isConditionalCheckFailedGraphQLError(error)) {
      throw error;
    }
  }
};

/**
 * Creates each reversal before cancelling its posting, under an id derived from the posting: a run interrupted
 * between the two writes leaves the posting active, and running it again reuses the existing reversal.
 */
export const reverseChargePostings = async (
  chargePostings: Posting[],
  createPosting: (input: CreatePostingInput) => Promise<PostingDTO>,
  updatePosting: (updates: UpdatePostingInput) => Promise<PostingDTO>
): Promise<void> => {
  await processInChunks(
    getChargePostingReversals(chargePostings),
    async ({ postingToCancel, reversalInput }) => {
      await createReversalUnlessAlreadyCreated(reversalInput, createPosting);
      await updatePosting({
        id: postingToCancel.id,
        _version: postingToCancel._version,
        status: PostingStatus.CANCELLED_BY_REVERSAL,
      });
    },
    POSTING_REVERSALS_CHUNK_SIZE
  );
};

export const getStatementIdsToRecalculate = (chargeStatements: ChargeStatement[]): string[] =>
  uniq(
    chargeStatements.reduce((acc: string[], chargeStatement) => {
      if (chargeStatement.statement?.status === StatementStatus.TO_VALIDATE) {
        acc.push(chargeStatement.statementId);
      }
      return acc;
    }, [])
  );

export const getChargeDeletionErrorMessageId = (error: unknown): string => {
  if (error instanceof ChargeDeletionBlockedByStatementError) {
    return CHARGE_DELETION_BLOCKED_BY_STATEMENT_MESSAGE_ID;
  }
  if (error instanceof ChargeDeletionBlockedByAffectationError) {
    return CHARGE_DELETION_BLOCKED_BY_AFFECTATION_MESSAGE_ID;
  }
  return GENERIC_API_ERROR_MESSAGE_ID;
};
